import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { FilterState } from '../../types/filters';
import { filterService } from '../../services/filterService';
import { toast } from '../ui/use-toast';

interface SaveFilterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentFilter: FilterState;
  onSuccess: () => void;
  existingFilterId?: string;
  existingFilterName?: string;
}

export function SaveFilterDialog({
  open,
  onOpenChange,
  currentFilter,
  onSuccess,
  existingFilterId,
  existingFilterName
}: SaveFilterDialogProps) {
  const [filterName, setFilterName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  
  // Set initial filter name if editing an existing filter
  useEffect(() => {
    if (open && existingFilterName) {
      setFilterName(existingFilterName);
    } else if (open) {
      setFilterName('');
    }
  }, [open, existingFilterName]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!filterName.trim()) {
      setError('Please enter a filter name');
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    
    try {
      // Check if name already exists (only for new filters)
      if (!existingFilterId) {
        const exists = await filterService.filterNameExists(filterName);
        if (exists) {
          setError('A filter with this name already exists');
          setIsSubmitting(false);
          return;
        }
      }
      
      // Save or update the filter
      if (existingFilterId) {
        await filterService.updateFilter(existingFilterId, filterName, currentFilter);
        toast({
          title: 'Filter updated',
          description: `Your filter "${filterName}" has been updated.`,
          variant: 'default'
        });
      } else {
        await filterService.saveFilter(filterName, currentFilter);
        toast({
          title: 'Filter saved',
          description: `Your filter "${filterName}" has been saved.`,
          variant: 'default'
        });
      }
      
      onOpenChange(false);
      onSuccess();
    } catch (err) {
      console.error('Error saving filter:', err);
      setError('Failed to save filter. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle dialog close
  const handleOpenChange = (newOpen: boolean) => {
    // Ensure we clean up properly when dialog closes
    if (!newOpen) {
      setIsSubmitting(false);
      setError('');
    }
    onOpenChange(newOpen);
  };
  
  return (
    <Dialog 
      open={open} 
      onOpenChange={handleOpenChange}
      modal={true} // Ensure it's modal to prevent interaction with the background
    >
      <DialogContent 
        className="sm:max-w-[425px] bg-white dark:bg-gray-800 dark:text-white"
        // Remove preventFocusManagement prop to ensure proper focus management
      >
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="dark:text-white">
              {existingFilterId ? 'Update Filter' : 'Save Filter'}
            </DialogTitle>
            <DialogDescription className="dark:text-gray-300">
              {existingFilterId 
                ? 'Update your saved filter with a new name or the current filter settings.'
                : 'Save your current filter settings for future use.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="filter-name" className="text-right dark:text-white">
                Name
              </Label>
              <Input
                id="filter-name"
                value={filterName}
                onChange={(e) => setFilterName(e.target.value)}
                className="col-span-3 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="My Filter"
                autoFocus
              />
            </div>
            
            {error && (
              <div className="text-sm text-red-500 dark:text-red-400 mt-2">{error}</div>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
              className="dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="dark:bg-blue-600 dark:hover:bg-blue-700"
            >
              {isSubmitting 
                ? 'Saving...' 
                : existingFilterId 
                  ? 'Update Filter' 
                  : 'Save Filter'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
